import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type AgentStats } from "../lib/api";
import { cn } from "../lib/cn";
import { ConfirmDialog } from "../components/ConfirmDialog";

interface Endpoint {
  id: string;
  name: string;
  kind: "litmus-receiver" | "local-disk" | "catalog-direct";
  baseUrl: string;
  enabled: boolean;
  createdAt: string;
}

const DEFAULT_DROPBOX_FALLBACK = "/var/lib/litmus-catalog-agent/dropbox";

const KIND_META: Record<
  string,
  { label: string; icon: React.ReactNode; tone: string }
> = {
  "catalog-direct": {
    label: "Catalog direct",
    tone: "bg-brand-50 text-brand-700 border-brand-500/30",
    icon: (
      <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
        <path
          d="M10 3L3 7v6l7 4 7-4V7l-7-4Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  "local-disk": {
    label: "Local disk",
    tone: "bg-amber-50 text-amber-700 border-amber-500/30",
    icon: (
      <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
        <rect
          x="3"
          y="6"
          width="14"
          height="10"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M3 10h14" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="6.5" cy="13" r="1" fill="currentColor" />
      </svg>
    ),
  },
  "litmus-receiver": {
    label: "Litmus receiver",
    tone: "bg-litmus-light-green-100 text-litmus-dark-green-700 border-litmus-light-green-300",
    icon: (
      <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
        <path
          d="M10 3v10M6 9l4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4 15h12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
};

export function EndpointsPage() {
  const qc = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"local-disk" | "catalog-direct">(
    "catalog-direct",
  );
  const [baseUrl, setBaseUrl] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editBaseUrl, setEditBaseUrl] = useState("");
  const [editAuthToken, setEditAuthToken] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<Endpoint | null>(null);

  const endpoints = useQuery({
    queryKey: ["endpoints"],
    queryFn: () => api.get<Endpoint[]>("/api/v1/endpoints"),
  });

  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.get<AgentStats>("/api/v1/stats"),
  });
  const defaultDropbox =
    stats.data?.defaultDropboxPath ?? DEFAULT_DROPBOX_FALLBACK;

  useEffect(() => {
    if (kind === "local-disk" && !advanced) setBaseUrl(defaultDropbox);
    else if (kind !== "local-disk" && baseUrl === defaultDropbox)
      setBaseUrl("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, advanced, defaultDropbox]);

  const create = useMutation({
    mutationFn: (body: unknown) => api.post("/api/v1/endpoints", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["endpoints"] });
      setShowForm(false);
      setName("");
      setBaseUrl("");
      setAuthToken("");
      setAdvanced(false);
      setCreateError(null);
    },
    onError: (err: unknown) =>
      setCreateError(err instanceof Error ? err.message : "create failed"),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.patch(`/api/v1/endpoints/${encodeURIComponent(id)}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["endpoints"] });
      setEditingId(null);
      setEditError(null);
    },
    onError: (err: unknown) =>
      setEditError(err instanceof Error ? err.message : "update failed"),
  });

  const setDefault = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/v1/endpoints/${encodeURIComponent(id)}/default`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["endpoints"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/v1/endpoints/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["endpoints"] }),
  });

  function startEdit(ep: Endpoint) {
    setEditingId(ep.id);
    setEditName(ep.name);
    setEditBaseUrl(ep.baseUrl);
    setEditAuthToken("");
    setEditError(null);
  }

  return (
    <div className="page-container">
      <header className="flex items-end justify-between gap-6 pt-8 pb-6">
        <div>
          <div className="eyebrow mb-1">Delivery</div>
          <h1 className="font-display text-[32px] font-semibold leading-tight tracking-tight text-ink">
            Ingest Endpoints
          </h1>
          <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-ash">
            Where the agent ships metadata.{" "}
            <strong className="text-ink">Catalog direct</strong> streams
            proposals live. <strong className="text-ink">Local disk</strong>{" "}
            drops encrypted files for air-gapped plants. Only one endpoint is
            active at a time.
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className={showForm ? "btn-secondary" : "btn-primary"}
        >
          {showForm ? (
            "Cancel"
          ) : (
            <>
              <span className="text-[15px] leading-none">+</span> Add endpoint
            </>
          )}
        </button>
      </header>

      {showForm && (
        <section className="mb-6 card p-6">
          <h2 className="mb-5 font-display text-[17px] font-semibold tracking-tight text-ink">
            New endpoint
          </h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Plant 01 catalog"
              />
            </Field>
            <Field label="Kind">
              <div className="grid grid-cols-2 gap-2">
                <KindChip
                  active={kind === "catalog-direct"}
                  onClick={() => setKind("catalog-direct")}
                  title="Catalog direct"
                  sub="Live REST"
                />
                <KindChip
                  active={kind === "local-disk"}
                  onClick={() => setKind("local-disk")}
                  title="Local disk"
                  sub="Standalone drop"
                />
              </div>
            </Field>
            {kind === "catalog-direct" ? (
              <>
                <Field label="Catalog URL" className="sm:col-span-2">
                  <input
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://catalog.litmus.io"
                    className="input font-mono text-[12.5px]"
                  />
                </Field>
                <Field label="Personal access token" className="sm:col-span-2">
                  <input
                    type="password"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    className="input font-mono text-[12.5px]"
                    placeholder="Catalog PAT — Settings → Access Tokens"
                  />
                </Field>
              </>
            ) : (
              <div className="sm:col-span-2 space-y-3">
                {!advanced ? (
                  <div className="rounded-md border border-rule bg-datahub-gray-1500 px-4 py-3 text-[13px]">
                    <div className="font-semibold text-ink">
                      Default destination
                    </div>
                    <div className="mt-1 font-mono text-[11.5px] text-ash">
                      {defaultDropbox}
                    </div>
                    <button
                      onClick={() => setAdvanced(true)}
                      className="mt-2 text-[12px] text-brand-700 hover:underline"
                    >
                      Use a different folder →
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Field label="Destination directory">
                      <input
                        value={baseUrl}
                        onChange={(e) => setBaseUrl(e.target.value)}
                        placeholder={defaultDropbox}
                        className="input font-mono text-[12.5px]"
                      />
                    </Field>
                    <button
                      onClick={() => {
                        setAdvanced(false);
                        setBaseUrl(defaultDropbox);
                      }}
                      className="text-[12px] text-brand-700 hover:underline"
                    >
                      ← Back to default
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {createError && (
            <div className="mt-4 rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {createError}
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => {
                setShowForm(false);
                setAdvanced(false);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => create.mutate({ name, kind, baseUrl, authToken })}
              disabled={create.isPending || !name || !baseUrl}
              className="btn-primary"
            >
              {create.isPending ? "Saving…" : "Save endpoint"}
            </button>
          </div>
        </section>
      )}

      <section className="space-y-3">
        {endpoints.isLoading &&
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="card h-20 animate-pulseSoft" />
          ))}
        {endpoints.data?.length === 0 && (
          <div className="card px-6 py-12 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-rule bg-surface text-ash">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                className="h-5 w-5"
                aria-hidden
              >
                <path
                  d="M10 3v10M6 9l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 15h12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p className="text-[13px] text-ash">
              No ingest endpoints configured. Without one, every artifact stays
              on disk.
            </p>
          </div>
        )}
        {endpoints.data?.map((e: Endpoint) => {
          const meta = KIND_META[e.kind] ?? KIND_META["catalog-direct"];
          return (
            <div key={e.id} className="card overflow-hidden">
              <div className="flex items-center gap-4 px-5 py-4">
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
                    meta.tone,
                  )}
                >
                  {meta.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold text-ink">
                      {e.name}
                    </span>
                    {e.enabled && (
                      <span className="chip-emerald">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{" "}
                        Default
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span
                      className={cn(
                        "rounded-full border px-1.5 py-0 font-mono text-[9.5px] uppercase tracking-wider",
                        meta.tone,
                      )}
                    >
                      {meta.label}
                    </span>
                    <span className="font-mono text-[11.5px] text-ash truncate">
                      {e.baseUrl}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    onClick={() => setDefault.mutate(e.id)}
                    disabled={e.enabled || setDefault.isPending}
                    className="btn-secondary text-[12px] py-1.5 px-3 disabled:opacity-40"
                  >
                    Set default
                  </button>
                  <button
                    onClick={() =>
                      editingId === e.id ? setEditingId(null) : startEdit(e)
                    }
                    className="btn-secondary text-[12px] py-1.5 px-3"
                  >
                    {editingId === e.id ? "Cancel" : "Edit"}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(e)}
                    className="btn-danger text-[12px] py-1.5 px-3"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {editingId === e.id && (
                <div className="border-t border-rule bg-datahub-gray-1500 px-5 py-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Name">
                      <input
                        value={editName}
                        onChange={(ev) => setEditName(ev.target.value)}
                        className="input"
                      />
                    </Field>
                    <Field label="Kind">
                      <div className="input flex items-center bg-surface font-mono text-[12px] text-ash">
                        {e.kind}
                      </div>
                    </Field>
                    <Field
                      label={
                        e.kind === "catalog-direct"
                          ? "Catalog URL"
                          : "Directory"
                      }
                      className="sm:col-span-2"
                    >
                      <input
                        value={editBaseUrl}
                        onChange={(ev) => setEditBaseUrl(ev.target.value)}
                        className="input font-mono text-[12.5px]"
                      />
                    </Field>
                    {e.kind !== "local-disk" && (
                      <Field
                        label="Access token (blank = keep current)"
                        className="sm:col-span-2"
                      >
                        <input
                          type="password"
                          value={editAuthToken}
                          onChange={(ev) => setEditAuthToken(ev.target.value)}
                          className="input font-mono text-[12.5px]"
                          placeholder="Leave blank to keep existing"
                        />
                      </Field>
                    )}
                  </div>
                  {editError && (
                    <div className="mt-3 rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                      {editError}
                    </div>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => setEditingId(null)}
                      className="btn-secondary text-[12px]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() =>
                        update.mutate({
                          id: e.id,
                          body: {
                            name: editName,
                            baseUrl: editBaseUrl,
                            ...(editAuthToken
                              ? { authToken: editAuthToken }
                              : {}),
                          },
                        })
                      }
                      disabled={update.isPending || !editName || !editBaseUrl}
                      className="btn-primary text-[12px]"
                    >
                      {update.isPending ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete endpoint"
        description={`Delete "${confirmDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
          setConfirmDelete(null);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-ash">
        {label}
      </span>
      {children}
    </label>
  );
}

function KindChip({
  active,
  onClick,
  title,
  sub,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  sub: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-md border px-3 py-2 text-left transition-colors",
        active
          ? "border-litmus-dark-green-700 bg-litmus-dark-green-700 text-white"
          : "border-rule bg-surface text-ash hover:border-ink/30 hover:text-ink",
      )}
    >
      <div className="text-[13px] font-semibold">{title}</div>
      <div className="text-[11px] opacity-70">{sub}</div>
    </button>
  );
}
